import { CircularProgress, Grid, Paper, useTheme } from "@mui/material";
import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { SocialReportDetail } from "core/types/social.types";
import { Text } from "components/atoms/text";
import { KeyValueInsights } from "components/atoms/key-value-insights";
import { grey } from "@mui/material/colors";
import { GrowthChart } from "components/molecules/growth-chart";
import { customOrange } from "core/theme/variants";
import { abbreviateNumber } from "core/helpers/abbreviate-number";

interface Props {
  isReportLoading: boolean;
  reportDetail?: SocialReportDetail;
}

const GrowthContent: FunctionComponent<Props> = ({
  isReportLoading,
  reportDetail,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const followersGrowth = reportDetail?.profileAdvancedInsights?.followersGrowth
    ? reportDetail?.profileAdvancedInsights?.followersGrowth
    : undefined;

  return (
    <div style={{ overflowX: "auto", width: "100%" }}>
      {isReportLoading ? (
        <div
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <CircularProgress color="secondary" size={"100px"} />
        </div>
      ) : (
        reportDetail && (
          <Paper
            sx={{
              borderRadius: "10px",
              paddingY: "20px",
            }}
          >
            <Text fontSize={"18px"} fontWeight={"600"} pb={"15px"}>
              {t("social-report-detail.growth.title")}
            </Text>

            <Grid container pb={"20px"}>
              <Grid item spacing={2} xs={12}>
                <KeyValueInsights
                  title={t("social-report-detail.growth.avg_per_day")}
                  content={
                    followersGrowth?.values.followersPerDayAvg.value
                      ? followersGrowth?.values.followersPerDayAvg.value + "%"
                      : t("empty_states.empty_stats.title")
                  }
                  size="small"
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} mt={"20px"}>
              <Grid item xs={12}>
                <GrowthChart
                  title={t("social-report-detail.audience.countries_title")}
                  growthValues={
                    followersGrowth
                      ? followersGrowth?.values.newFollowersPerDay.values
                      : []
                  }
                />
              </Grid>
            </Grid>

            {reportDetail.profileAdvancedInsights.topMentions.value && (
              <Grid container spacing={4} mt={"20px"}>
                <Grid item xs={12}>
                  <Text
                    padding={"30px 0px 20px 0px"}
                    fontWeight={700}
                    style={{
                      color: grey[700],
                    }}
                    fontSize={"12px"}
                  >
                    {t("social-report-detail.growth.mentioned_by")}
                  </Text>
                  {reportDetail.profileAdvancedInsights.topMentions.value?.map(
                    (m) => (
                      <Grid
                        item
                        xs={12}
                        sm={4}
                        md={3}
                        display={"flex"}
                        flexDirection={"column"}
                        justifyContent={"center"}
                        alignItems={"center"}
                        sx={{
                          border: `0.5px solid ${theme.palette.divider}`,
                          padding: "15px",
                          borderRadius: "8px",
                        }}
                      >
                        <Text
                          sx={{
                            color: customOrange[700],
                          }}
                        >
                          {"@" + m.username}
                        </Text>
                        <Text>
                          {abbreviateNumber(m.followersCount) + " followers"}
                        </Text>
                        <Text>
                          {m.numMentions +
                            " " +
                            (m.numMentions === 1
                              ? t("social-report-detail.growth.mention")
                              : t("social-report-detail.growth.mentions"))}
                        </Text>
                      </Grid>
                    )
                  )}
                </Grid>
              </Grid>
            )}
          </Paper>
        )
      )}
    </div>
  );
};

export default GrowthContent;
